// React and hooks
import React, { useMemo } from "react";

// Third-party libraries
import AnimateHeight from "react-animate-height";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown } from "@fortawesome/pro-solid-svg-icons";
import { Spinner } from "react-bootstrap";

// UI components
import Button from "../../../../ui-elements/Button/Button";
import DisplayBox from "../../../../ui-elements/DisplayBox/DisplayBox";
import Table from "../../../../ui-elements/Table/Table";
import VerticalTable from "../../../../ui-elements/Table/VerticalTable";

// Types
import { AnswerListOverviewDto, Person } from "../../../../types/CompanyAnswersTypes";
import { IKeldanApiResponse } from "../../../../types/KeldanTypes";

// Services and constants
import { boardMemberTypes } from "../Constants";
import { useBuyingProcess } from "../../../../services/buyhook";
import { GET_KELDAN_API_URL } from "../../../../services/config";
import { convertToPercentage, displayAddress, formatNumber, getTableDate, insertDash, isCompany } from "../../../../services/utils";

interface IProps {
    data: AnswerListOverviewDto | null;
    /**
     * If provided and true, the section will open. Useful for parent-controlled scenarios
     * like forcing it open when an error state occurs. Acts as a one-way trigger; users can still toggle afterwards.
     */
    initialOpen?: boolean;
}


const GildandiSkraningGrunnupplysingar: React.FC<IProps> = ({ data, initialOpen }) => {
    const [isOpen, setIsOpen] = React.useState<boolean>(initialOpen ?? false);

    // Track if we've already reacted to initialOpen
    const openedByPropRef = React.useRef(false);

    // Open only once when initialOpen becomes true (do not force reopen after manual close)
    React.useEffect(() => {
        if (initialOpen && !openedByPropRef.current) {
            setIsOpen(true);
            openedByPropRef.current = true;
        }
    }, [initialOpen]);

    const boardOnly = useMemo(() => {
        // Early return if data or nested properties don't exist
        if (!data?.keldanReport?.mappedReport?.adilarfelags) {
            return false;
        }

        // At this point we know adilarfelags exists, so we can safely work with it
        const adilarfelags = data.keldanReport.mappedReport.adilarfelags;
        // since we get all the board members we need to filter filter the ones that dont have -1 stada
        // Check if any board member has a kennitala adila that matches our board member types
        // We're looking at the first character of the kennitala adila (SSN) to determine the type
        const boardMemberIndex = adilarfelags.filter(x => x.stada !== "-1").findIndex(member => {
            return boardMemberTypes.includes(member.kennadila[0]);
        }
        );

        // Return true if we found at least one board member (index !== -1)
        return boardMemberIndex !== -1;
    }, [data]);

    const { tryPurchaseItem, isItemLoading, resetState } = useBuyingProcess(
        async (itemString: string) => {
            try {
                const headers = {
                    'Content-Type': 'application/json; charset=utf-8'
                };
                const ssn = itemString.split(';')[0];
                const url = `${GET_KELDAN_API_URL()}/Einstaklingar/Hlutafelagathatttaka-Stakt-Kaupa/${ssn}`;
                const response = await fetch(url, {
                    method: 'POST',
                    headers
                });
                if (response.ok) {
                    if (response.redirected) {
                        window.location.href = response.url;
                        return;
                    }
                    const responseBody: IKeldanApiResponse = await response.json();
                    if (responseBody.message) {
                        resetState({
                            type: 'alert',
                            headText: responseBody.message
                        });
                    } else {
                        resetState('bad request')
                    }
                } else {
                    resetState('response not ok');
                }
            } catch (e) {
                resetState('network error');
            }
        },
        'hlutafélagaskrá'
    );

    const getButton = (buttonSsn: string, title: string, nrOfCompanies: number | null) => {
        if (isCompany(buttonSsn)) {
            return <Button
                size='sm'
                buttonType='secondary'
                anchorProps={{ href: `/Fyrirtaeki/Yfirlit/${buttonSsn}` }}
            >
                Skoða
            </Button>
        }
        return <div className='button-wrapper'>
            <Button
                showLoader={isItemLoading(`${buttonSsn};${title}`)}
                size='sm'
                buttonType='primary'
                onClick={() => {
                    if (title) {
                        tryPurchaseItem(`${buttonSsn};${title}`);
                    }
                }}
            >
                Sækja
            </Button>
            {nrOfCompanies !== null && nrOfCompanies !== -1 &&
                <span className='company-count extra-info'>
                    {nrOfCompanies} {nrOfCompanies % 10 === 1 && nrOfCompanies % 100 !== 11 ? 'félag' : 'félög'}
                </span>
            }
        </div>
    }
    const getNumberOfCompanies = (ssn?: string) => {
        if (ssn === undefined || data instanceof Error) return null;
        return data?.keldanReport?.mappedReport?.adilarfelags?.find(member => member.kennadila === ssn)?.nrOfCompanies ?? null;
    }
    const displayTableRow = (
        index: number,
        title: string,
        flex: number,
        addresses: boolean,
        member: Person
    ) => {
        const { nafn, heimili, postnr, postfang, kennitala } = member;
        const nrOfCompanies = getNumberOfCompanies(kennitala);
        return (
            <tr
                key={index}
                className='lineContainer'
            >
                <th>
                    <strong>{(index !== 0) ? '' : title}</strong>
                </th>
                <td style={{ 'flex': flex }}>
                    <div className="text-wrapper">
                        {(nafn)
                            ? (
                                <p className={cx({ 'nameAndSocial': kennitala })}>
                                    {nafn}
                                    {kennitala && ', ' + insertDash(kennitala)}
                                </p>
                            )
                            : (
                                <p>-</p>
                            )
                        }
                        {(addresses) && <p className='address'>{displayAddress(heimili, postnr, postfang)}</p>}
                    </div>
                    {(kennitala) && getButton(kennitala, title, nrOfCompanies)}
                </td>
            </tr>
        )
    }

    //show Loading
    if (data === null) {
        return <DisplayBox
            id="grunnupplysingar"
            className="gs_grunnUppl"
            title="Gildandi skráning - grunnupplýsingar"
            asideComponent={
                <Spinner className='loading-spinner' animation="border" />
            }
        />
    }
    if (data !== null && data?.keldanReport?.mappedReport === undefined) {
        return null;
    }
    if (data.customerSSN === null) {
        return null;
    }

    return <DisplayBox
        onClick={() => isOpen === false && setIsOpen(true)}
        id="grunnupplysingar"
        className={cx("gs_grunnUppl", { 'open': isOpen })}
        titleComponent={<span className="gs_grunnUppl--title" onClick={() => setIsOpen(!isOpen)}>Gildandi skráning - grunnupplýsingar</span>}
        asideComponent={<div className="aside-wrapper">
            <p className="italic-text">Gögn sótt: {getTableDate(data.keldanReport.created_timestamp, "DD/MM/YYYY", ".")}</p>
            <FontAwesomeIcon
                icon={faAngleDown}
                className={cx("angle-down-icon", { 'open': isOpen })}
                onClick={() => setIsOpen(!isOpen)}
            />
        </div>
        }
    >
        <AnimateHeight
            className="dagatal-item__content"
            duration={300}
            height={isOpen ? 'auto' : 0}
        >
            {
                data === null ? (
                    <div>Sæki gögn...</div>
                ) : (
                    <div>
                        <div className="vertical-table-wrapper">
                            <VerticalTable fallbackSymbol
                                data={[data?.keldanReport?.mappedReport]}
                                tableSize="lg"
                                columns={[{
                                    title: 'Nafn',
                                    renderCell: ({ hlutafelag }) => hlutafelag?.fulltheiti ?? '-'
                                }, {
                                    title: 'Kennitala',
                                    renderCell: ({ hlutafelag }) => insertDash(hlutafelag?.kennitala),
                                }, {
                                    title: 'Lögheimili',
                                    renderCell: ({ hlutafelag }) => displayAddress(hlutafelag?.logheimili, hlutafelag?.pnr_LOGHEIM, hlutafelag?.pofang_LOGH),
                                }, {
                                    title: 'Síðast breytt í fyrirtækjaskrá RSK',
                                    renderCell: ({ hlutafelag }) => hlutafelag && getTableDate(new Date(hlutafelag.dag_SID_BR.timeInMillis), 'DD/MM/YYYY', '.'),
                                }
                                    , {
                                    title: 'Atvinnugrein (ISAT)',
                                    renderCell: ({ isatfelags }) => isatfelags && isatfelags[0] ? `${isatfelags[0].isatkodi ?? '-'} ${isatfelags[0].lysing ?? isatfelags[0].lysingens ?? '-'} ` : '-',
                                },
                                {
                                    title: 'Tilgangur',
                                    renderCell: ({ hlutafelag }) => hlutafelag?.tilgangur ?? '-',
                                }, {
                                    title: 'Rekstrarform',
                                    renderCell: ({ hlutafelag }) => hlutafelag?.rformlysing ?? '-',
                                }, {
                                    title: 'Stofnað',
                                    renderCell: ({ hlutafelag }) => hlutafelag && getTableDate(new Date(hlutafelag.dag_SKRAD.timeInMillis), 'DD/MM/YYYY', '.'),
                                }, {
                                    title: 'Hlutafé',
                                    renderCell: ({ hlutafelag }) => hlutafelag ? `${formatNumber(hlutafelag?.hlutafe, '-', 0)} ${hlutafelag?.gjmid_HLUTAF ?? '-'}` : '-',
                                }, {
                                    title: 'Dags. samþykkta',
                                    renderCell: ({ hlutafelag }) => hlutafelag && getTableDate(new Date(hlutafelag.dags_SAMTH.timeInMillis), 'DD/MM/YYYY', '.'),
                                }
                                ]}
                            />
                            <VerticalTable
                                fallbackSymbol
                                horizontalFlexSize={2}
                                tableSize="lg"
                                data={[data?.keldanReport?.mappedReport]}
                                headerTitle='Hlutafélagaþátttaka'
                                columns={boardOnly ? [{
                                    title: 'Síðasta breyting á stjórn',
                                    renderCell: ({ hlutafelag }) => hlutafelag && getTableDate(new Date(hlutafelag.dag_STJRFUND.timeInMillis), 'DD/MM/YYYY', '.'),
                                }, {
                                    title: 'Firmað rita',
                                    renderCell: ({ hlutafelag }) => hlutafelag?.firm_RITA,
                                }, {
                                    title: 'Prókúruumboð',
                                    overrideDefaultStyle: true,
                                    renderCell: ({ addresses, prokuruhafar }) =>
                                        (prokuruhafar === null || prokuruhafar.length === 0)
                                            ? '-'
                                            : prokuruhafar.map((item, index) => displayTableRow(index, 'Prókúruumboð', 2, addresses, item)),
                                }, {
                                    title: 'Framkvæmdastjórn',
                                    overrideDefaultStyle: true,
                                    renderCell: ({ addresses, framkvaemdarstjorar }) =>
                                        (framkvaemdarstjorar === null || framkvaemdarstjorar.length === 0)
                                            ? '-'
                                            : framkvaemdarstjorar.map((item, index) => displayTableRow(index, 'Framkvæmdastjórn', 2, addresses, item)),
                                },
                                {
                                    title: 'Stjórn',
                                    overrideDefaultStyle: true,
                                    renderCell: (row) => {
                                        const { adilarfelags } = row || {};
                                        return (adilarfelags === null || adilarfelags?.length === 0)
                                            ? '-'
                                            : '-'
                                    }
                                }, {
                                    title: 'Endurskoðendur',
                                    overrideDefaultStyle: true,
                                    renderCell: ({ addresses, endurskodendur }) =>
                                        (endurskodendur === null || endurskodendur.length === 0)
                                            ? '-'
                                            : endurskodendur.map((item, index) => displayTableRow(index, 'Endurskoðendur', 2, addresses, item)),
                                }, {
                                    title: 'Hömlur á meðferð hlutabréfa',
                                    renderCell: ({ hlutafelag }) => hlutafelag?.homlur
                                        ? hlutafelag.homlur === '1'
                                            ? 'Já'
                                            : hlutafelag.homlur === "0"
                                                ? 'Nei'
                                                : '-'
                                        : '-',
                                }, {
                                    title: 'Innlausnarskylda',
                                    renderCell: ({ hlutafelag }) => hlutafelag?.lausnarsk
                                        ? hlutafelag.lausnarsk === '1'
                                            ? 'Já'
                                            : hlutafelag.lausnarsk === "0"
                                                ? 'Nei'
                                                : '-'
                                        : '-',
                                }, {
                                    title: 'Stofnendur',
                                    overrideDefaultStyle: true,
                                    renderCell: ({ addresses, stofnendur }) =>
                                        (stofnendur === null || stofnendur.length === 0)
                                            ? '-'
                                            : stofnendur.map((item, index) => displayTableRow(index, 'Stofnendur', 2, addresses, item)),
                                }] : [{
                                    title: 'Stjórnarformaður',
                                    renderCell: ({ addresses, stjornarformadur }) =>
                                        (stjornarformadur === null || [stjornarformadur].length === 0)
                                            ? '-'
                                            : [stjornarformadur].map((item, index) => displayTableRow(index, 'Stjórnarformaður', 2, addresses, item)),
                                    overrideDefaultStyle: true,
                                }, {
                                    title: 'Meðstjórnendur',
                                    overrideDefaultStyle: true,
                                    renderCell: ({ addresses, stjornendur }) =>
                                        (stjornendur === null || stjornendur.length === 0)
                                            ? '-'
                                            : stjornendur.map((item, index) => displayTableRow(index, 'Meðstjórnendur', 2, addresses, item)),
                                }, {
                                    title: 'Varamenn',
                                    overrideDefaultStyle: true,
                                    renderCell: ({ addresses, varamenn }) =>
                                        (varamenn === null || varamenn.length === 0)
                                            ? '-'
                                            : varamenn.map((item, index) => displayTableRow(index, 'Varamenn', 2, addresses, item)),
                                }, {
                                    title: 'Síðasta breyting á stjórn',
                                    renderCell: ({ hlutafelag }) => hlutafelag && getTableDate(new Date(hlutafelag.dag_STJRFUND.timeInMillis), 'DD/MM/YYYY', '.'),
                                }, {
                                    title: 'Firmað rita',
                                    renderCell: ({ hlutafelag }) => hlutafelag?.firm_RITA,
                                }, {
                                    title: 'Prókúruumboð',
                                    overrideDefaultStyle: true,
                                    renderCell: ({ addresses, prokuruhafar }) =>
                                        (prokuruhafar === null || prokuruhafar.length === 0)
                                            ? '-'
                                            : prokuruhafar.map((item, index) => displayTableRow(index, 'Prókúruumboð', 2, addresses, item)),
                                }, {
                                    title: 'Framkvæmdastjórn',
                                    overrideDefaultStyle: true,
                                    renderCell: ({ addresses, framkvaemdarstjorar }) =>
                                        (framkvaemdarstjorar === null || framkvaemdarstjorar.length === 0)
                                            ? '-'
                                            : framkvaemdarstjorar.map((item, index) => displayTableRow(index, 'Framkvæmdastjórn', 2, addresses, item)),
                                }, {
                                    title: 'Endurskoðendur',
                                    overrideDefaultStyle: true,
                                    renderCell: ({ addresses, endurskodendur }) =>
                                        (endurskodendur === null || endurskodendur.length === 0)
                                            ? '-'
                                            : endurskodendur.map((item, index) => displayTableRow(index, 'Endurskoðendur', 2, addresses, item)),
                                }, {
                                    title: 'Hömlur á meðferð hlutabréfa',
                                    renderCell: ({ hlutafelag }) => hlutafelag?.homlur
                                        ? hlutafelag.homlur === '1'
                                            ? 'Já'
                                            : hlutafelag.homlur === "0"
                                                ? 'Nei'
                                                : '-'
                                        : '-',
                                }, {
                                    title: 'Innlausnarskylda',
                                    renderCell: ({ hlutafelag }) => hlutafelag?.lausnarsk
                                        ? hlutafelag.lausnarsk === '1'
                                            ? 'Já'
                                            : hlutafelag.lausnarsk === "0"
                                                ? 'Nei'
                                                : '-'
                                        : '-',
                                }, {
                                    title: 'Stofnendur',
                                    overrideDefaultStyle: true,
                                    renderCell: ({ addresses, stofnendur }) =>
                                        (stofnendur === null || stofnendur.length === 0)
                                            ? '-'
                                            : stofnendur.map((item, index) => displayTableRow(index, 'Stofnendur', 2, addresses, item)),
                                }]
                                }
                            />
                        </div>
                        {(data.keldanReport?.mappedReport?.owners && data.keldanReport.mappedReport.owners.length > 0)
                            && (
                                <div className="raunverulegir-eigendur-wrapper">
                                    <h4>Raunverulegir eigendur</h4>
                                    <Table
                                        tableSize="lg"
                                        showEmptyTableText
                                        data={data.keldanReport.mappedReport.owners}
                                        columns={[{
                                            title: 'Nafn',
                                            renderCell: ({ nafn }) => nafn,
                                            textAlign: 'left',
                                        }, {
                                            title: 'Fæðingarár/mán.',
                                            renderCell: ({ faedingarmanudur }) => faedingarmanudur,
                                            textAlign: 'right',
                                        }, {
                                            title: 'Búsetuland',
                                            renderCell: ({ busetuland }) => busetuland,
                                            textAlign: 'right',
                                        }, {
                                            title: 'Ríkisfang',
                                            renderCell: ({ rikisfang }) => rikisfang,
                                            textAlign: 'right',
                                        }, {
                                            title: 'Eignarhlutur',
                                            renderCell: ({ eignarhlutur }) => convertToPercentage(eignarhlutur / 100, true, 2),
                                            textAlign: 'right',
                                        }]}
                                    />
                                </div>
                            )}
                    </div>
                )
            }
        </AnimateHeight>

    </DisplayBox>
}
export default GildandiSkraningGrunnupplysingar;